import {createFileRoute, redirect} from "@tanstack/react-router";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent} from "./-routeComponent";
import {getPlatformAuthorizationDetailsWithCaching} from "~/utilities/queries";
import type {PlatformAuthorizationDetails} from "./-types";
import {getPostLoginRedirectPathFromLocation} from "~/utilities/postLoginRedirect";

export const Route = createFileRoute("/_withAuthenticationStatus/_authenticated")({
    beforeLoad: async ({context, location}) => {
        if (context.authenticationStatus.isAuthenticated !== true) {
            console.groupCollapsed("User is not authenticated, redirecting to sign-in");
            console.log("authenticationStatus", context.authenticationStatus);
            console.groupEnd();

            const redirectTo = getPostLoginRedirectPathFromLocation({
                location: location,
                intellsysBaseUrl: context.intellsysBaseUrl,
            });
            throw redirect({
                to: "/sign-in",
                search: {
                    redirectTo: redirectTo,
                },
            });
        }

        const platformAuthorizationDetailsResult = await getPlatformAuthorizationDetailsWithCaching({
            accessToken: context.authenticationStatus.accessToken,
            queryClient: context.queryClient,
            intellsysBaseUrl: context.intellsysBaseUrl,
        });
        if (platformAuthorizationDetailsResult.success !== true) {
            console.groupCollapsed("Failed to get platform authorization details, defaulting to null");
            console.log("platformAuthorizationDetailsResult", platformAuthorizationDetailsResult);
            console.groupEnd();

            const platformAuthorizationDetails: PlatformAuthorizationDetails = {
                isPlatformAdmin: false,
            };

            return {
                authenticationStatus: context.authenticationStatus,
                platformAuthorizationDetails: platformAuthorizationDetails,
            };
        }

        if (hasAnyPlatformAccess(platformAuthorizationDetailsResult.data) !== true) {
            const platformAuthorizationDetails: PlatformAuthorizationDetails = {
                isPlatformAdmin: false,
            };

            return {
                authenticationStatus: context.authenticationStatus,
                platformAuthorizationDetails: platformAuthorizationDetails,
            };
        }

        const platformAuthorizationDetails: PlatformAuthorizationDetails = {
            isPlatformAdmin: true,
            platformAccessMatrix: platformAuthorizationDetailsResult.data,
        };

        return {
            authenticationStatus: context.authenticationStatus,
            platformAuthorizationDetails: platformAuthorizationDetails,
        };
    },
    loader: async ({context}) => {
        return {
            heimdallUser: context.authenticationStatus.heimdallUser,
            platformAuthorizationDetails: context.platformAuthorizationDetails,
        };
    },
    staleTime: Infinity,
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});

function hasAnyPlatformAccess(platformAccessMatrix: Record<string, Record<string, boolean>>): boolean {
    return Object.values(platformAccessMatrix).some((operationAccessMatrix) =>
        Object.values(operationAccessMatrix).some((hasAccess) => hasAccess === true),
    );
}
